import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { captionElapsedMs, type CaptionPlayback } from "./caption-playback";
import { validatedVisuals, visualAt, visualVideoSeconds, visualOpacity, type EditionVisualCue } from "./edition-visuals";
import { EditionVisualPreloader, visualPreloadWindow } from "./edition-visual-preload";
import type { EpisodeLine } from "./stage-api";
import { clearHostMediaScale, updateHostMediaScale } from "./edition-host-occlusion";
import "./edition-visual-screen.css";

/** Uses the same exact audio/text clock as captions, never a free-running slideshow. */
export function EditionVisualScreen({ playback, paused, motionEnabled, lines, recording = false, preview, enabled = Boolean(playback) }: {
  playback?: CaptionPlayback | null; paused: boolean; motionEnabled: boolean;
  lines?: readonly EpisodeLine[]; enabled?: boolean; recording?: boolean; preview?: EditionVisualCue | null;
}) {
  const [, refresh] = useState(0);
  const [preloader] = useState(() => new EditionVisualPreloader(() => refresh(v => v + 1)));
  const [visible, setVisible] = useState(!document.hidden);
  const connection = (navigator as Navigator & { connection?: EventTarget & { saveData?: boolean } }).connection;
  const [saveData, setSaveData] = useState(Boolean(connection?.saveData));
  useEffect(() => {
    const visibility = () => setVisible(!document.hidden);
    const data = () => setSaveData(Boolean(connection?.saveData));
    document.addEventListener("visibilitychange", visibility);
    connection?.addEventListener("change", data);
    return () => { document.removeEventListener("visibilitychange", visibility); connection?.removeEventListener("change", data); preloader.clear(); };
  }, [connection, preloader]);
  const [active, setActive] = useState<{ playback: CaptionPlayback; cue: EditionVisualCue } | null>(null);
  useLayoutEffect(() => {
    setActive(null);
    if (!enabled || !visible) { preloader.clear(); return; }
    if (preview && !playback) {
      // Framing review downloads one pinned still/poster, never plays a clip.
      preloader.reconcile([{ url: preview.kind === "video" ? preview.posterUrl! : preview.url,
        hash: preview.kind === "video" ? preview.posterSha256! : preview.sha256, video: false }]);
      return;
    }
    const cues = playback ? validatedVisuals(playback.line) : [];
    let frame = 0;
    const update = () => {
      if (playback && !playback.isCurrent()) { preloader.clear(); setActive(null); return; }
      const elapsed = playback?.pending ? 0 : playback ? captionElapsedMs(playback) : 0;
      preloader.reconcile(visualPreloadWindow(lines ?? (playback ? [playback.line] : []), playback?.line, elapsed,
        motionEnabled && !saveData, !paused && !saveData));
      const cue = playback && !playback.pending ? visualAt(cues, elapsed) : null;
      setActive(old => old && old.playback === playback && old.cue === cue ? old : cue && playback ? { playback, cue } : null);
      frame = requestAnimationFrame(update);
    };
    update();
    return () => cancelAnimationFrame(frame);
  }, [playback, lines, enabled, visible, motionEnabled, saveData, paused, preloader, preview]);
  if (enabled && visible && preview && !playback) return <Visual key={`preview:${preview.id}:${preview.url}`} cue={preview}
    paused motionEnabled={false} preloader={preloader} recording={recording} />;
  if (!active || active.playback !== playback) return null;
  return <Visual key={`${active.cue.id}:${active.cue.url}`} cue={active.cue} playback={playback}
    paused={paused} motionEnabled={motionEnabled && !saveData} preloader={preloader} recording={recording} />;
}

function Visual({ cue, playback, paused, motionEnabled, preloader, recording }: {
  cue: EditionVisualCue; playback?: CaptionPlayback; paused: boolean; motionEnabled: boolean;
  preloader: EditionVisualPreloader; recording: boolean;
}) {
  const figure = useRef<HTMLElement>(null);
  const picture = useRef<HTMLDivElement>(null);
  const readyAt = useRef<number | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [imageReady, setImageReady] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [portrait, setPortrait] = useState(false);
  const [imageAspect, setImageAspect] = useState(1);
  const [videoAspect, setVideoAspect] = useState(1);
  const [visible, setVisible] = useState(!document.hidden);
  const connection = (navigator as Navigator & { connection?: EventTarget & { saveData?: boolean } }).connection;
  const [saveData, setSaveData] = useState(Boolean(connection?.saveData));
  useEffect(() => {
    const visibility = () => setVisible(!document.hidden);
    const data = () => setSaveData(Boolean(connection?.saveData));
    document.addEventListener("visibilitychange", visibility);
    connection?.addEventListener("change", data);
    return () => { document.removeEventListener("visibilitychange", visibility); connection?.removeEventListener("change", data); };
  }, [connection]);
  const imageSource = preloader.source(cue.kind === "video" ? cue.posterUrl! : cue.url);
  const videoSource = preloader.source(cue.url);
  const allowVideo = Boolean(playback) && cue.kind === "video" && motionEnabled && !saveData && !videoFailed && visible && Boolean(videoSource);
  const mediaAspect = allowVideo && videoReady ? videoAspect : imageAspect;
  useLayoutEffect(() => {
    const node = figure.current, scene = node?.closest('.studio-scene');
    if (!node || !scene || !imageReady) return;
    let frame = 0;
    const update = () => { updateHostMediaScale(node, mediaAspect); frame = requestAnimationFrame(update); };
    update();
    return () => { cancelAnimationFrame(frame); clearHostMediaScale(scene); };
  }, [imageReady, mediaAspect]);
  useLayoutEffect(() => {
    const frame = picture.current, node = figure.current;
    if (!imageReady || !frame || !node) return;
    const fitCredit = () => {
      // Match object-fit: contain, including transparent side margins.
      const width = Math.min(frame.clientWidth, frame.clientHeight * mediaAspect);
      node.style.setProperty("--edition-media-credit-width", `${width}px`);
    };
    fitCredit();
    const observer = new ResizeObserver(fitCredit);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [imageReady, mediaAspect]);
  useLayoutEffect(() => {
    if (!imageReady) return;
    if (!playback) { if (figure.current) figure.current.style.opacity = "1"; return; }
    let frame = 0;
    const update = () => {
      const node = figure.current;
      if (!node) return;
      node.style.opacity = String(playback.isCurrent()
        ? visualOpacity(cue, captionElapsedMs(playback), readyAt.current ?? cue.atMs, motionEnabled) : 0);
      frame = requestAnimationFrame(update);
    };
    update();
    return () => cancelAnimationFrame(frame);
  }, [cue, playback, imageReady, motionEnabled]);
  useLayoutEffect(() => { setVideoReady(false); }, [allowVideo]);
  useEffect(() => {
    const node = video.current;
    if (!node || !allowVideo || !playback) return;
    let frame = 0, disposed = false, pending = false;
    let lastElapsed = captionElapsedMs(playback), lastProgressAt = performance.now();
    const update = () => {
      if (disposed) return;
      if (!playback.isCurrent()) { node.pause(); return; }
      const elapsed = captionElapsedMs(playback);
      if (elapsed !== lastElapsed) { lastElapsed = elapsed; lastProgressAt = performance.now(); }
      const target = visualVideoSeconds(cue, elapsed);
      const hold = paused || Boolean(playback.audio?.paused) || performance.now() - lastProgressAt > 200 || elapsed < cue.atMs || elapsed >= cue.untilMs;
      if (node.readyState >= 1) {
        // A short clip holds its last frame; it never loops unrelated footage.
        const end = Number.isFinite(node.duration) ? Math.max(0, node.duration - .04) : target;
        const position = Math.min(target, end);
        // Let a seek finish before requesting another one. Reissuing it every
        // animation frame can starve decoding, especially on mobile browsers.
        if (!node.seeking && Math.abs(node.currentTime - position) > .2) node.currentTime = position;
        if (hold || target >= end) node.pause();
        else if (node.paused && !pending) {
          pending = true;
          void node.play().catch(() => { if (!disposed) setVideoFailed(true); }).finally(() => { pending = false; });
        }
      }
      frame = requestAnimationFrame(update);
    };
    update();
    return () => { disposed = true; cancelAnimationFrame(frame); node.pause(); };
  }, [allowVideo, cue, playback, paused]);
  if (imageFailed || !imageSource) return null; // Pending or failed media never stops the dialogue.
  return <figure ref={figure} style={{ opacity: 0 }} className={`edition-visual edition-visual--${cue.layout}`} data-ready={imageReady} data-portrait={portrait}
    aria-label={cue.alt}>
    <div ref={picture} className="edition-visual-picture">
      <img src={imageSource} alt={cue.alt} decoding="async"
        onLoad={event => { const image = event.currentTarget; readyAt.current ??= playback ? captionElapsedMs(playback) : cue.atMs; setPortrait(image.naturalHeight > image.naturalWidth); setImageAspect(image.naturalWidth / image.naturalHeight); setImageReady(true); }} onError={() => setImageFailed(true)} />
      {allowVideo && <video ref={video} src={videoSource} muted playsInline preload="metadata" aria-hidden="true"
        data-ready={videoReady} onLoadedMetadata={event => { const clip = event.currentTarget; setPortrait(clip.videoHeight > clip.videoWidth); if (clip.videoWidth && clip.videoHeight) setVideoAspect(clip.videoWidth / clip.videoHeight); }} onLoadedData={() => {
          const node = video.current;
          if (node && playback && Math.abs(node.currentTime - visualVideoSeconds(cue, captionElapsedMs(playback))) < .2) setVideoReady(true);
        }} onSeeked={() => setVideoReady(true)} onError={() => setVideoFailed(true)} />}
    </div>
    <figcaption><span>{cue.context}</span>{recording ? <span className="edition-visual-credit">{cue.credit}</span>
      : <a href={cue.sourceUrl} target="_blank" rel="noopener noreferrer">{cue.credit}</a>}</figcaption>
  </figure>;
}
